import type { Infer } from "superstruct";
import { enums, is, record, string } from "superstruct";
import { getDateInXYears } from "@/utils/getDateInXYears";
import { browserCookie } from "@/utils/browserCookie";
import type { Immutable } from "@/types/immutable";
import { getRecordEntries } from "@/utils/getRecordEntries";
import { COOKIES_KEYS } from "@/constants/cookies.constants";
import { decodeSuperstructResult } from "@/utils/decodeSuperstructResult";
import { MatchPredictionVariant } from "../utils/matchPrediction.types";
import { remoteLogger } from "@/utils/remoteLogger";
import { FETCH_METHOD } from "@/constants/fetch-method";
import type { MatchPredictionResponse } from "../utils/matchPrediction.types";
import { matchPredictionResponseStruct } from "../utils/matchPredictionResponse.structs";
import { envBasedConfig } from "@/constants/env-based-config.constant";

const matchPredictionsCookieRecordStruct = record(
  string(),

  enums([
    MatchPredictionVariant.Home,
    MatchPredictionVariant.Draw,
    MatchPredictionVariant.Away,
  ]),
);

type MatchPredictionsCookieRecord = Immutable<
  Infer<typeof matchPredictionsCookieRecordStruct>
>;

export class MatchPredictionService {
  private readonly matchIdToPrediction = new Map<
    string,
    MatchPredictionVariant
  >();

  constructor() {
    getRecordEntries(this.predictionsInCookie).forEach(
      ([matchId, userPrediction]) => {
        this.matchIdToPrediction.set(matchId, userPrediction);
      },
    );
  }

  /**
   * deviceId generates a random string to be sent to the Eristic API. The
   * server keeps track of device ids to not allow multiple opinions from the
   * same device. Of course, this can easily be abused on the web because there
   * is no device id. We don't even try to pretend we have one and store it in
   * the cookies. Instead we will store a map of match ids and predictions made.
   * If the user has already predicted for a given match we disable the buttons.
   * If mock data is enabled on this environment we return the same `mock-id`
   * so that we can intercept a network request and mock the response.
   */

  private get deviceId(): string {
    return Math.random().toString(36).substring(2, 7);
  }

  private get predictionsInCookie(): MatchPredictionsCookieRecord {
    const rawValue = browserCookie.get(COOKIES_KEYS.MatchPrediction);

    if (rawValue === undefined) {
      return {};
    }

    const parsed: unknown = JSON.parse(rawValue);
    if (is(parsed, matchPredictionsCookieRecordStruct)) {
      return parsed;
    }

    const warnMessage = "Could not parse cookie value for match predictions";

    remoteLogger.error(warnMessage);

    return {};
  }

  public async predict(
    matchId: string,
    userPrediction: MatchPredictionVariant,
  ): Promise<{ home: number; draw: number; away: number }> {
    const apiUrl = `${envBasedConfig.webExperience}/polls-api/opinions/threeway/${matchId}/${this.deviceId}`;
    const opinion = {
      [MatchPredictionVariant.Home]: "teamHome",
      [MatchPredictionVariant.Draw]: "teamDraw",
      [MatchPredictionVariant.Away]: "teamAway",
    }[userPrediction];

    const options: RequestInit = {
      method: FETCH_METHOD.POST,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ opinion }),
    };

    this.setPrediction(matchId, userPrediction);

    return fetch(apiUrl, options)
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        return response.json();
      })
      .then((response) =>
        decodeSuperstructResult<MatchPredictionResponse>(
          response,
          matchPredictionResponseStruct,
        ),
      )
      .then((response) => {
        if ("value" in response) {
          const { data } = response.value;

          return {
            home: data.teamHome ?? 0,
            draw: data.teamDraw ?? 0,
            away: data.teamAway ?? 0,
          };
        }

        throw new Error("Invalid response structure");
      })
      .catch((error) => {
        const errorMessage = `Could not make "${userPrediction}" prediction for match id "${matchId}"`;
        remoteLogger.error(errorMessage, error);

        return { home: 0, draw: 0, away: 0 };
      });
  }

  public getPrediction(matchId: string): MatchPredictionVariant | undefined {
    return this.matchIdToPrediction.get(matchId);
  }

  private setPrediction(
    matchId: string,
    userPrediction: MatchPredictionVariant,
  ): void {
    // mutate in-memory cache
    this.matchIdToPrediction.set(matchId, userPrediction);

    // persist user's prediction in cookies so that we render properly in SSR
    browserCookie.put({
      key: COOKIES_KEYS.MatchPrediction,
      value: JSON.stringify({
        ...this.predictionsInCookie,
        [matchId]: userPrediction,
      }),
      expires: getDateInXYears(2),
    });
  }
}
